import { useEffect, useRef, useState, useMemo, type ComponentProps, type ReactNode } from 'react';
import styles from './LegalCases.module.scss';
import { fetchLegalCasesData } from '../../../../../store/dashboard/tables/tables_apiHelpers';
import { DATE_FORMATS, formatDateByFormat, getTimeStamp } from '../../../../../utils/dateUtils';
import {
    HEADERS,
    STATUS,
    isHighConfidence,
    hasConfidenceSignal,
    getColumns,
    CASE_TYPE_TONE_CLASS,
    GROUP_BADGE_CLASS,
    COMPANY_NAME_PATTERN,
    ROW_MOTION_MS,
    ROW_MOTION_MAX_ROWS,
    LEGAL_CASES_FETCHING_TEXT,
    LEGAL_DIRECTION,
    LEGAL_DIRECTION_PARAM,
    isDefaulterCase,
    formatDefaulterAmount,
    formatDefaulterCaseType,
    isClosedLegalCase,
    DEFAULTER_EMPTY_VALUE,
} from './legalCasesConstants';
import type { CaseParty, Column, Direction, GroupId, LegalCasesData } from './legalCasesType';
import { readLegalCasesSummary } from './legalCaseSummary';
import LegalCaseSummaryCards from './LegalCaseSummaryCards';
import CaseTypeBar from './CaseTypeBar';
import CaseDetailsDrawer from './CaseDetailsDrawer/CaseDetailsDrawer';
import { mapCaseToDetails } from './mapCaseToDetails';
import type { CaseDocument } from './CaseDetailsDrawer/types';
import { useCaseDetailsDrawer } from './CaseDetailsDrawer/useCaseDetailsDrawer';
import { useScrollLinkedSections } from '../../../../../common/hooks/useScrollLinkedSections';
import { useDeferredUnmount } from '../../../../../common/hooks/useDeferredUnmount';
import { useReanchorOnReflow } from '../../../../../common/hooks/useReanchorOnReflow';
import { flattenAndLimitCases } from './flattenAndLimitCases';
import { formatDesignationWithShareholding } from '../../../../../utils/formatShareholdingPercent';
import clsx from 'clsx';
import {
    convertToCapitalizeUnderscore,
    convertToFirstLetterCapital,
    decodeHtmlEntities,
    getSubdomain,
    normalizeNameKey,
} from '../../../../../utils/utils';
import { DATA_FETCH_STATUS } from '../../../../../store/dashboard/constants';
import CtaPill from '../../../../../common/_custom/CtaPill/CtaPill';
import { EXCEL_ANALYSIS_LABEL, EXCEL_ANALYSIS_LOADING_LABEL } from '../../../../../common/_custom/CtaPill/ctaPillConstants';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { useParams, useSearchParams } from 'react-router-dom';
import { holdRowHighlight, ROW_FOCUS_HIGHLIGHT_CLASS, useFocusRowFromUrlParam } from '../../../../../utils/customHooks';
import { getFolderStructure } from '../../../../../store/dataRoom/apiHelper';
import { toast } from 'react-toastify';
import RiskIndicators from '../../../../RiskIndicators/RiskIndicators';
import type { RegulatoryRiskCell, Row as RiskIndicatorRow } from '../../../../RiskIndicators/RiskIndicators';
import { resolveLegalCasesGroupId } from '../../../../RiskIndicators/legalCasesRiskState';
import { LEGAL_AND_REGULATORY_NOTE_BODY } from '../../../../RiskIndicators/RiskIndicatorConstants';
import A4Page from '../../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../../store/pdfReport/pdfReport_constants';
import AicaOverview from '../../../../../common/AicaOverview/AicaOverview';
import { TabModule } from '../../../../../utils/constants/commonConstants';
import { removeTooltipContent, setTooltipContent } from '../../../../../store/tooltip/tooltip_actions';
import { userBlack, companyBlack, changeCircleBlue } from '../../../../../assets/hostedassets';
import { caseCategoryLabel, caseCategoryOf, caseCategoryTone } from './caseCategory';
import { AICA_LEGAL_CASE_DOWNLOAD, AICA_LEGAL_CASE_DOWNLOAD_ALL } from '../../../../../utils/constants/mixpanelEvents/investorEvents';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import HeaderBodyBox from '../../../../../common/HeaderBodyBox/HeaderBodyBox';
import CategorySelector from '../../../../../common/CategorySelector/CategorySelector';
import FileViewerDrawer from '../../../../../common/DocumentViewer/FileViewerDrawer';
import { EXT_MIME, openBlobViewer, closeBlobViewer, BlobViewerState } from '../../../../../common/DocumentViewer/constants';
import CaseDocumentPreviewer from './CaseDocumentPreviewer';
import { workbookToXlsxBlob, EXCEL_GENERATION_FAILED_MESSAGE } from '../../../../../common/_custom/DataTable/utils/dataTableExportUtils';
import { useLegalCasesPolling } from './useLegalCasesPolling';
import { useLatestRequestGuard } from '../../../../../common/hooks/useLatestRequestGuard';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import FetchFailureIcon from '../../../../../common/_custom/FetchFailureIcon/FetchFailureIcon';
import { FETCH_FAILURE_TEXT } from '../../../../../utils/constants/taskTrackerStatus';

type Groups = Record<GroupId, any[]>;

const GROUP_CONFIG: { id: GroupId; title: string }[] = [
    { id: 'OPEN', title: 'Open Cases' },
    { id: 'DISPOSED', title: 'Disposed Cases' },
    { id: 'OTHER', title: 'Other Probable Cases' },
];

const visibleGroups = (showOtherGroup: boolean) => (showOtherGroup ? GROUP_CONFIG : GROUP_CONFIG.filter(({ id }) => id !== 'OTHER'));

// Direction toggle order + labels — plain, count-free. The open-case count renders as a separate
// pill via CategorySelector's `count`/`pillVariant` props, not baked into the label string. Order
// is explicit (not derived from object key insertion order) since it drives what the user sees
// left-to-right.
const DIRECTION_ORDER: readonly { id: Direction; label: string }[] = [
    { id: 'filed_against', label: 'Filed Against' },
    { id: 'filed_by', label: 'Filed By' },
];

// Max entities shown inline in a Filed Against/By cell before collapsing the rest into "+N more".
const MAX_VISIBLE_ENTITIES = 2;

function LegalCases({
    assesseeOrgId,
    companyName,
    initialiseHeadlinePanelPolling,
    isPDFReport = false,
}: {
    assesseeOrgId: string;
    companyName: string;
    initialiseHeadlinePanelPolling?: Function;
    isPDFReport?: boolean;
}) {
    const dispatch = useAppDispatch();
    const [legalCasesData, setLegalCasesData] = useState<null | LegalCasesData>();
    // Gates the summary boxes: a fetch that never resolved or a permanently-failed FIRST
    // load must never render a confident green "0 Cases" — see the render gate near `render`.
    const [legalCasesLoadState, setLegalCasesLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
    // Stamped with the org the last-resolved response belongs to — the org-switch effect below
    // resets legalCasesLoadState to 'loading' one render AFTER the switch commit, so on that
    // commit itself legalCasesLoadState still reads 'ready' from the PREVIOUS org. hasResponse
    // (passed to useLegalCasesPolling) checks this against the live assesseeOrgId at render time
    // so the grace window can't arm off a stale response that belongs to the org being left.
    const [responseOrgId, setResponseOrgId] = useState<string | null>(null);
    const [viewerState, setViewerState] = useState<BlobViewerState | null>(null);
    const [viewingDocId, setViewingDocId] = useState('');
    const [viewingDocLabel, setViewingDocLabel] = useState('');
    const [generatingExcel, setGeneratingExcel] = useState(false);
    const [searchParams] = useSearchParams();
    // 'regulatoryChecks' targets the Risk Indicators (AML/PEP) section; default lands on Legal Cases rows.
    const focusSection = searchParams.get('section') ?? '';
    const isRegulatorySection = focusSection === 'regulatoryChecks';
    const focusDirectorParam = searchParams.get('directorName') ?? '';
    const focusDirection: Direction =
        searchParams.get(LEGAL_DIRECTION_PARAM) === LEGAL_DIRECTION.FILED_BY ? LEGAL_DIRECTION.FILED_BY : LEGAL_DIRECTION.FILED_AGAINST;
    const hasFocusParam = !!focusDirectorParam || !!searchParams.get('directorDin');
    // A deep link opens on the direction it came from, so the focus hook can never land on the other one first.
    const [activeDirection, setActiveDirection] = useState<Direction>(() =>
        hasFocusParam && !isRegulatorySection ? focusDirection : LEGAL_DIRECTION.FILED_AGAINST,
    );
    // Single-open accordion by product ask: at most one group is expanded at a time.
    const [expandedGroup, setExpandedGroup] = useState<GroupId | null>('OPEN');
    const { selectedCase, caseForDetails, openCaseDrawer, closeCaseDrawer, isFetchingCaseDetails, caseDetailsFetchFailed } =
        useCaseDetailsDrawer(assesseeOrgId);
    const lastFetchedDate = legalCasesData?.lastFetchedDate || null;
    // Tracks the focusDirectorParam value the deep-link sync effect below has already applied — a
    // poll refresh changes groupsFiledAgainst/groupsFiledBy identity but must NOT re-run the sync
    // (it would clobber a group the user manually collapsed, or a direction they switched to).
    const deepLinkAppliedRef = useRef<string | null>(null);
    const userData = useAppSelector((state) => state.user.userData);
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const params = useParams();
    const [deepLinkDriven, setDeepLinkDriven] = useState(hasFocusParam);

    const sortableDate = (singleCase: any): string =>
        formatDateByFormat(
            isDefaulterCase(singleCase) ? singleCase.filingDate : singleCase?.decisionDate?.trim() || singleCase?.nextHearingDate?.trim(),
            DATE_FORMATS.ISO,
        );

    useReanchorOnReflow({
        active: !isPDFReport && deepLinkDriven,
        findTarget: () => document.querySelector<HTMLElement>(`.${ROW_FOCUS_HIGHLIGHT_CLASS}`),
        onReanchor: holdRowHighlight,
    });

    const sortLatestFirst = (cases: any[]) =>
        [...cases].sort((a, b) => {
            const byDate = sortableDate(b).localeCompare(sortableDate(a));
            if (byDate !== 0) return byDate;
            if (a.groupName === 'Company' && b.groupName !== 'Company') return -1;
            if (a.groupName !== 'Company' && b.groupName === 'Company') return 1;
            return 0;
        });

    const getFlatSortedCases = (casesData: any[]): any[] => {
        if (!casesData || !Array.isArray(casesData)) return [];
        return sortLatestFirst(flattenAndLimitCases(casesData, isPDFReport));
    };

    const OTHER_STATUS_RANK: Record<string, number> = { [STATUS.PENDING.value]: 0, [STATUS.DISPOSED.value]: 2 };
    const otherStatusRank = (singleCase: any): number => OTHER_STATUS_RANK[singleCase?.status] ?? 1;

    const groupCases = (casesData: any[]): Groups => {
        const groups: Groups = { OPEN: [], DISPOSED: [], OTHER: [] };
        getFlatSortedCases(casesData).forEach((singleCase) => {
            if (!isHighConfidence(singleCase)) {
                groups.OTHER.push(singleCase);
            } else if (isClosedLegalCase(singleCase)) {
                groups.DISPOSED.push(singleCase);
            } else {
                groups.OPEN.push(singleCase);
            }
        });
        groups.OTHER.sort((a, b) => otherStatusRank(a) - otherStatusRank(b));
        return groups;
    };

    // Flat (ungrouped) lists — used only for the "Excel Analysis" export, which lists every case
    // regardless of confidence/status the same way it always has.
    const legalCasesAgainstCompanyOrDirector = useMemo(
        () => getFlatSortedCases(legalCasesData?.legalCasesFiledAgainstCompanyOrDirector || []),
        [legalCasesData?.legalCasesFiledAgainstCompanyOrDirector, isPDFReport],
    );

    const legalCasesByCompanyOrDirector = useMemo(
        () => getFlatSortedCases(legalCasesData?.legalCasesFiledByCompanyOrDirector || []),
        [legalCasesData?.legalCasesFiledByCompanyOrDirector, isPDFReport],
    );

    // Gates the "Other Probable Cases" bucket: only shown once the backend actually sends a
    // confidenceLevel signal for at least one case in either direction — reuses the already-flattened
    // lists above instead of re-flattening the raw payload.
    const hasConfidenceData = useMemo(
        () => hasConfidenceSignal([...legalCasesAgainstCompanyOrDirector, ...legalCasesByCompanyOrDirector]),
        [legalCasesAgainstCompanyOrDirector, legalCasesByCompanyOrDirector],
    );

    const hasCaseRows = legalCasesAgainstCompanyOrDirector.length > 0 || legalCasesByCompanyOrDirector.length > 0;

    const visibleGroupIds = useMemo(() => visibleGroups(hasConfidenceData).map(({ id }) => id), [hasConfidenceData]);

    const expandedGroupIds = useMemo(() => visibleGroupIds.filter((id) => id === expandedGroup), [visibleGroupIds, expandedGroup]);

    const { heldKeys: collapsingGroupIds, hold, release } = useDeferredUnmount<GroupId>(ROW_MOTION_MS);

    const setGroupExpanded = (groupId: GroupId, expand: boolean) => {
        if (expand || !groupAnimates(groupId)) release(groupId);
        else hold(groupId);
        if (expand && expandedGroup != null && expandedGroup !== groupId) {
            const sibling = expandedGroup;
            if (groupAnimates(sibling)) hold(sibling);
            else release(sibling);
        }
        setDeepLinkDriven(false);
        setExpandedGroup((prev) => {
            if (expand) return groupId;
            return prev === groupId ? null : prev;
        });
    };

    // Disabled — scroll must never toggle groups; kept only for the section registry scrollToGroup uses.
    const { scrollContainerRef, sectionRef, getSectionElement } = useScrollLinkedSections<GroupId>({
        sectionKeys: visibleGroupIds,
        expandedKeys: expandedGroupIds,
        onToggle: setGroupExpanded,
        enabled: false,
    });

    const groupsFiledAgainst = useMemo(
        () => groupCases(legalCasesData?.legalCasesFiledAgainstCompanyOrDirector || []),
        [legalCasesData?.legalCasesFiledAgainstCompanyOrDirector, isPDFReport],
    );

    const groupsFiledBy = useMemo(
        () => groupCases(legalCasesData?.legalCasesFiledByCompanyOrDirector || []),
        [legalCasesData?.legalCasesFiledByCompanyOrDirector, isPDFReport],
    );

    const activeGroups = activeDirection === 'filed_against' ? groupsFiledAgainst : groupsFiledBy;

    // Clicking a Legal Cases pill (either column) switches to that column's own direction and
    // scrolls to the matching section instead of opening a drawer. The group is resolved by
    // resolveLegalCasesGroupId — the five-state machine's own mapping once polus has shipped the
    // split counts, or today's Confirmed/Probable-only rule (kept verbatim) for a legacy-shape cell.
    const scrollToGroup = (_row: RiskIndicatorRow, cell: RegulatoryRiskCell, direction: Direction) => {
        const groupId: GroupId = resolveLegalCasesGroupId(cell, direction);
        setActiveDirection(direction);
        setGroupExpanded(groupId, true);
        requestAnimationFrame(() => getSectionElement(groupId)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    };

    const groupAnimates = (groupId: GroupId) => !deepLinkDriven && activeGroups[groupId].length <= ROW_MOTION_MAX_ROWS;

    // Memoized concat of the HIGH-confidence groups feeding CaseTypeBar — keeps the array reference
    // stable across renders so CaseTypeBar's own internal useMemo isn't busted every render.
    const activeCaseTypeBarCases = useMemo(
        () => [...activeGroups.OPEN, ...activeGroups.DISPOSED],
        [activeGroups.OPEN, activeGroups.DISPOSED],
    );

    const filedAgainstCaseTypeBarCases = useMemo(
        () => [...groupsFiledAgainst.OPEN, ...groupsFiledAgainst.DISPOSED],
        [groupsFiledAgainst.OPEN, groupsFiledAgainst.DISPOSED],
    );

    const filedByCaseTypeBarCases = useMemo(
        () => [...groupsFiledBy.OPEN, ...groupsFiledBy.DISPOSED],
        [groupsFiledBy.OPEN, groupsFiledBy.DISPOSED],
    );

    // An org switch without a remount (TabSelector doesn't key its Outlet — see the ladder's
    // org-switch precondition) must not let org A's rows or in-flight response leak into org B:
    // reset first so B shows the loader, then guard the fetch so a late A response is dropped.
    useEffect(() => {
        setLegalCasesData(undefined);
        setLegalCasesLoadState('loading');
        fetchData();
    }, [assesseeOrgId]);

    useEffect(() => {
        if (modulePollingList.includes(TabModule.LEGAL_CHECKS)) {
            fetchData();
        }
    }, [modulePollingList]);

    useFocusRowFromUrlParam({
        ready: !isPDFReport && !!legalCasesData && (isRegulatorySection || hasCaseRows),
        paramKey: 'directorName',
        dataAttr: isRegulatorySection ? 'ri-director' : 'lc-director',
        fallbackParamKey: 'directorDin',
        fallbackDataAttr: isRegulatorySection ? 'ri-din' : undefined,
        extraParamKeys: [LEGAL_DIRECTION_PARAM],
    });

    // The deep-linked row can live in a collapsed group and/or the inactive direction — switch to the
    // matching direction and expand its group before the focus hook above tries to scroll to it.
    useEffect(() => {
        if (isPDFReport || !legalCasesData || !focusDirectorParam || isRegulatorySection) return;
        if (deepLinkAppliedRef.current === focusDirectorParam) return;
        const target = normalizeNameKey(focusDirectorParam);
        const findGroupId = (groups: Groups): GroupId | null =>
            GROUP_CONFIG.find(({ id }) => groups[id].some((c) => normalizeNameKey(c.groupName) === target))?.id ?? null;

        // The direction the click came from is tried first; the other is the fallback.
        const directions: Array<[Direction, Groups]> =
            focusDirection === LEGAL_DIRECTION.FILED_BY
                ? [
                      [LEGAL_DIRECTION.FILED_BY, groupsFiledBy],
                      [LEGAL_DIRECTION.FILED_AGAINST, groupsFiledAgainst],
                  ]
                : [
                      [LEGAL_DIRECTION.FILED_AGAINST, groupsFiledAgainst],
                      [LEGAL_DIRECTION.FILED_BY, groupsFiledBy],
                  ];
        for (const [direction, groups] of directions) {
            const match = findGroupId(groups);
            if (match) {
                setActiveDirection(direction);
                setDeepLinkDriven(true);
                setExpandedGroup(match);
                deepLinkAppliedRef.current = focusDirectorParam;
                return;
            }
        }
    }, [legalCasesData, focusDirectorParam, focusDirection, isRegulatorySection, isPDFReport, groupsFiledAgainst, groupsFiledBy]);

    // Stale-response guard: the token covers same-org supersession — it is bumped by every new
    // request, so a same-org response can only be current if no later request for that org has
    // since started. It does NOT cover the route-change gap: assesseeOrgId can flip to a new org
    // before that org's own switch effect has started its request, since a resolved promise's
    // continuation is a microtask that can run before React's passive effects flush — so a stale
    // org's response can still read as "current" by token alone. The activeOrgIdRef check below
    // covers that gap.
    const legalCasesRequestGuard = useLatestRequestGuard();

    // Assigned at render time (not in an effect) so it already reflects the new org on the very
    // commit assesseeOrgId changes — an effect-based assignment would land one render late.
    const activeOrgIdRef = useRef('');
    activeOrgIdRef.current = assesseeOrgId;

    const fetchData = async () => {
        const token = legalCasesRequestGuard.startRequest();
        await fetchLegalCasesData(
            {
                assesseeOrgId: assesseeOrgId,
                requestId: getTimeStamp(),
            },
            {
                onSuccess: (data) => {
                    if (!legalCasesRequestGuard.isCurrent(token) || assesseeOrgId !== activeOrgIdRef.current) return;
                    setLegalCasesData(data);
                    setLegalCasesLoadState('ready');
                    setResponseOrgId(assesseeOrgId);
                },
                onError: () => {
                    if (!legalCasesRequestGuard.isCurrent(token) || assesseeOrgId !== activeOrgIdRef.current) return;
                    setLegalCasesLoadState('error');
                    setResponseOrgId(assesseeOrgId);
                },
            },
        );
    };

    const { isPolling, isFetchFailed } = useLegalCasesPolling({
        assesseeOrgId,
        legalCasesStatus: legalCasesData?.status,
        hasResponse: legalCasesLoadState !== 'loading' && responseOrgId === assesseeOrgId,
        hasCaseRows,
        fetchData,
        initialiseHeadlinePanelPolling,
        isPDFReport,
    });

    // Still needed for the Excel export's "Case Status" column — the in-table status pill was removed
    // (the group a case sits in already conveys Open/Disposed).
    const getStatusText = (status: string) => {
        switch (status) {
            case STATUS.DISPOSED.value:
                return 'CLOSED';
            case STATUS.PENDING.value:
                return 'OPEN';
            case STATUS.NOT_AVAILABLE.value:
                return '-';
        }
    };

    const getDisplayDate = (singleCase: any, forExcel: boolean = false) => {
        const { decisionDate, nextHearingDate } = singleCase || {};
        const isDefaulter = isDefaulterCase(singleCase);

        const { date, message } = isDefaulter
            ? { date: singleCase.filingDate, message: '' }
            : decisionDate?.trim()
            ? { date: decisionDate, message: 'Last Judgement' }
            : nextHearingDate?.trim()
            ? { date: nextHearingDate, message: 'Next Hearing' }
            : { date: '', message: '' };

        const emptyDate = isDefaulter ? DEFAULTER_EMPTY_VALUE : '-';
        const formattedDate = formatDateByFormat(date, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY) || (forExcel ? '' : emptyDate);

        return forExcel && message ? `${message}: ${formattedDate}` : formattedDate;
    };

    const trackCaseDownload = (caseType) =>
        Mixpanel.track(AICA_LEGAL_CASE_DOWNLOAD, {
            parentId: userData?.investorOrgId,
            websiteUrl: window.location.href,
            userId: userData?.userId,
            userRole: userData?.accountType,
            caseType: caseType,
            companyOrgId: params.id,
        });

    const viewSingleCaseDocument = (docId: string, caseDocuments: CaseDocument[]) => {
        setViewingDocLabel(caseDocuments.find((doc) => doc.docId === docId)?.label ?? '');
        setViewingDocId(docId);
    };

    const downloadCaseFunction = async (docIds, name, caseType) => {
        const validDocIds = docIds.filter((docId) => docId && docId.trim && docId.trim() !== '');
        trackCaseDownload(caseType);
        const fileNodes = validDocIds.map((docId) => ({
            name: null,
            type: 'file',
            innerFiles: [],
            docId: docId,
            updatedAt: Date.now(),
        }));
        const payload = {
            parentPath: 'CaseFiles',
            requestId: getTimeStamp(),
            fileNodes: fileNodes,
            downloadByOrgId: userData?.investorOrgId,
        };
        const res = await getFolderStructure(payload);
        if (res) {
            let blob = new Blob([res], { type: 'octet/stream' });
            let fileName = `${name}.zip`;
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
            toast.success('Folder Downloaded Successfully');
        } else {
            toast.error('Error in fetching folder');
        }
    };

    const reorderNames = (text: string, groupName: string): string => {
        if (!text || !groupName) return text || '';
        if (!text.trim() || !groupName.trim()) return text;

        const nameParts = groupName.toLowerCase().split(/\s+/);
        const originalParts = text.split(',').map((part) => part.trim());
        const validParts = originalParts.filter((part) => part.length > 0);

        const matching: string[] = [];
        const nonMatching: string[] = [];

        validParts.forEach((part) => {
            const lowerPart = part.toLowerCase();
            if (nameParts.some((name) => lowerPart.includes(name))) {
                matching.push(part);
            } else {
                nonMatching.push(part);
            }
        });

        return [...matching, ...nonMatching].join(', ');
    };

    const createWorksheetHeaders = (worksheet: ExcelJS.Worksheet, companyName: string, titleText: string) => {
        worksheet.mergeCells('A1:G1');
        worksheet.getCell('A1').value = companyName;
        worksheet.getCell('A1').font = { bold: true, size: 14 };

        worksheet.mergeCells('A2:G2');
        worksheet.getCell('A2').value = titleText;
        worksheet.getCell('A2').font = { bold: true };

        worksheet.addRow([]);
    };

    const addHeaderRow = (worksheet: ExcelJS.Worksheet, headers: string[]) => {
        const headerRow = worksheet.addRow(headers);
        headerRow.height = 25;
        headerRow.eachCell((cell) => {
            cell.font = { size: 11, bold: true };
            cell.alignment = { indent: 1, vertical: 'middle' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD8EAD2' },
            };
        });
    };

    const addDataRows = (
        worksheet: ExcelJS.Worksheet,
        cases: any[],
        companyName: string,
        getStatusText: Function,
        reorderNames: Function,
        decodeHtmlEntities: Function,
    ) => {
        const dataRows = cases.map((singleCase) => {
            const groupName = singleCase.groupName === 'Company' ? companyName : singleCase.groupName;
            const isDefaulter = isDefaulterCase(singleCase);
            return [
                isDefaulter ? (isClosedLegalCase(singleCase) ? 'CLOSED' : 'OPEN') : getStatusText(singleCase.status) || '',
                singleCase.caseNum || '',
                singleCase.court || '',
                reorderNames(decodeHtmlEntities(singleCase.filedAgainst), groupName) || '',
                reorderNames(decodeHtmlEntities(singleCase.filedBy), groupName) || '',
                (isDefaulter
                    ? formatDefaulterCaseType(singleCase.standardCaseType, singleCase.defaulterAmount)
                    : singleCase.caseCategory) || '',
                getDisplayDate(singleCase, true) || '',
            ];
        });

        dataRows.forEach((rowData) => {
            const row = worksheet.addRow(rowData);
            row.height = 25;
            row.eachCell((cell) => {
                cell.font = { size: 11 };
                cell.alignment = { vertical: 'middle', indent: 1 };
            });
        });
    };

    const generateExcelBlob = async (): Promise<Blob | null> => {
        const workbook = new ExcelJS.Workbook();

        const headers = ['Case Status', 'Case No.', 'Court', 'Filed Against', 'Filed By', 'Case Category', 'Date'];
        const columnWidths = [15, 30, 35, 50, 50, 35, 20];

        const addSheet = (sheetName, cases, titleText) => {
            const worksheet = workbook.addWorksheet(sheetName);
            createWorksheetHeaders(worksheet, companyName, titleText);
            addHeaderRow(worksheet, headers);
            addDataRows(worksheet, cases, companyName, getStatusText, reorderNames, decodeHtmlEntities);

            worksheet.columns.forEach((col, i) => {
                col.width = columnWidths[i] || 20;
            });
        };

        addSheet('Filed Against Cases', legalCasesAgainstCompanyOrDirector, 'Legal cases filed against Company / Director');
        addSheet('Filed By Cases', legalCasesByCompanyOrDirector, 'Legal cases filed by Company / Director');

        try {
            return await workbookToXlsxBlob(workbook);
        } catch {
            toast.error(EXCEL_GENERATION_FAILED_MESSAGE);
            return null;
        }
    };

    const trackDownloadAll = () => {
        Mixpanel.track(AICA_LEGAL_CASE_DOWNLOAD_ALL, {
            userRole: userData?.accountType,
            pnoPlatform: getSubdomain(),
            userId: userData?.userId,
            currentUrl: window.location.href,
            legalCasesByCount: legalCasesByCompanyOrDirector.length,
            legalCasesAgainstCount: legalCasesAgainstCompanyOrDirector.length,
            organizationId: params.id,
        });
    };

    const handleViewExcel = async () => {
        if (generatingExcel) return;
        setGeneratingExcel(true);
        try {
            const blob = await generateExcelBlob();
            if (!blob) return;
            setViewerState(openBlobViewer(blob, viewerState?.url));
            trackDownloadAll();
        } finally {
            setGeneratingExcel(false);
        }
    };

    const handleDownloadFromDrawer = () => {
        if (!viewerState?.blob) return;
        saveAs(viewerState.blob, `Legal_Cases_${convertToCapitalizeUnderscore(companyName)}.xlsx`);
    };

    const closeViewer = () => {
        setViewerState(closeBlobViewer(viewerState));
    };

    const renderRiskIndicators = () => <RiskIndicators isPDFReport={isPDFReport} onOpenLegalCases={scrollToGroup} />;

    // The frontend does NO arithmetic — it renders the backend's
    // legalCasesCountSummary verbatim, or its same-footprint skeleton, or nothing at all. Never a
    // fallback zero: a summary genuinely absent from an otherwise-successful response (the
    // frontend-ahead-of-polus deploy window) renders nothing, same as a failed first load.
    const legalCasesCountSummary = readLegalCasesSummary(legalCasesData);
    const isLegalCasesProcessing = legalCasesData?.status === DATA_FETCH_STATUS.PROCESSING;

    // Each direction's Total Confirmed Open case count renders as a separate "{n} Open" pill next
    // to its (unchanged) label, via CategorySelector's `count`/`countLabel`/`pillVariant` props.
    // Sourced from groupsFiledAgainst.OPEN / groupsFiledBy.OPEN — the SAME arrays the "Open Cases"
    // section badge already renders as "{n} Matches" (renderGroupHeaderRow, casesInGroup.length).
    // Reusing that exact array means the toggle pill and the section badge can never disagree —
    // there is only one source of truth, computed once by groupCases, not a backend field
    // duplicating an arithmetic the frontend already does.
    // No pill at all while loading/processing/absent (mirrors renderLegalCaseSummaryCards' gate
    // above) — a real 0 still renders as a grey "0 Open" pill once data has arrived.
    // Mirrors CategorySelector's own categoryOptions element shape rather than a hand-written
    // duplicate, so this can't drift from the component's real prop type.
    type DirectionOption = ComponentProps<typeof CategorySelector>['categoryOptions'][number] & { id: Direction };
    const hasLegalCasesData = legalCasesLoadState !== 'loading' && !isLegalCasesProcessing && legalCasesData != null;
    const directionOptions = useMemo<DirectionOption[]>(() => {
        const openCasesByDirection: Record<Direction, number> = {
            filed_against: groupsFiledAgainst.OPEN.length,
            filed_by: groupsFiledBy.OPEN.length,
        };
        return DIRECTION_ORDER.map(({ id, label }) => ({
            id,
            label,
            countLabel: 'Open',
            ...(hasLegalCasesData && {
                count: openCasesByDirection[id],
                pillVariant: openCasesByDirection[id] > 0 ? ('red' as const) : ('grey' as const),
            }),
        }));
    }, [groupsFiledAgainst, groupsFiledBy, hasLegalCasesData]);
    const renderLegalCaseSummaryCards = () => {
        if (isFetchFailed) return null;
        if (legalCasesLoadState === 'loading' || isLegalCasesProcessing) return <LegalCaseSummaryCards.Skeleton />;
        if (legalCasesData == null || legalCasesCountSummary == null) return null;
        return <LegalCaseSummaryCards summary={legalCasesCountSummary} />;
    };

    // Splits a comma-separated Filed Against/By string into individual entity names. When `primaryName`
    // is given (this is the direction's ACTIVE column, so we know the row's own entity), it's prepended
    // and stripped out of the rest of the string first. Stripped via normalizeNameKey rather than an
    // exact substring match — `decoded` comes from the raw case payload (which can carry a leading "*"
    // low-confidence marker) while `primaryName` doesn't, so an exact `.replace()` would silently fail
    // to match and render the same person twice.
    // The party array is authoritative when the vendor sends it: it is already one entry per party,
    // so it needs no comma-splitting (a party name can itself contain commas).
    const buildEntityList = (rawValue: string | undefined, primaryName?: string, parties?: CaseParty[] | null): string[] => {
        const fromParties = (parties ?? []).map((party) => party.name?.trim()).filter((name): name is string => !!name);
        const names = fromParties.length
            ? fromParties.map((name) => convertToFirstLetterCapital(decodeHtmlEntities(name)))
            : convertToFirstLetterCapital(decodeHtmlEntities(rawValue) || '-')
                  .split(',')
                  .map((value) => value.trim())
                  .filter(Boolean);
        const primaryKey = primaryName ? normalizeNameKey(primaryName) : '';
        const remainder = names.filter((value) => !primaryKey || normalizeNameKey(value) !== primaryKey);
        return primaryName ? [primaryName, ...remainder] : remainder;
    };

    // Shared renderer for BOTH Filed Against and Filed By cells (active or secondary column) — width
    // constrained (see .EntityCell/.EntityRow), shows up to MAX_VISIBLE_ENTITIES plain icon+name rows
    // (no chip/pill background), stacked one per line, then "+N more".
    // The row's OWN primary entity has a real entity-type signal (legalCase.name) and uses `icon`;
    // every co-party falls back to the COMPANY_NAME_PATTERN heuristic.
    const renderEntityCell = (
        rawValue: string | undefined,
        icon: string,
        primaryName?: string,
        designationSuffix?: string,
        parties?: CaseParty[] | null,
    ) => {
        const entities = buildEntityList(rawValue, primaryName, parties);
        const iconFor = (name: string, index: number) =>
            index === 0 && primaryName ? icon : COMPANY_NAME_PATTERN.test(name) ? companyBlack : userBlack;

        if (isPDFReport) {
            return (
                <span className={styles.NamePill}>
                    <img src={icon} height={'14px'} alt="" />
                    {entities.join(', ')}
                    {designationSuffix && <span className={styles.DesignationText}>{designationSuffix}</span>}
                </span>
            );
        }

        const visible = entities.slice(0, MAX_VISIBLE_ENTITIES);
        const overflowCount = entities.length - visible.length;
        // Only attach the lone-entity hover tooltip when there's nothing else to fold into "+N more".
        const showOwnTooltip = !!primaryName && entities.length <= 1;
        const fullListTooltip = entities.map((name, i) => (i === 0 && primaryName ? `${name}${designationSuffix || ''}` : name)).join(', ');

        return (
            <div className={styles.EntityCell}>
                {visible.map((name, i) => {
                    const isLastVisible = i === visible.length - 1;
                    return (
                        <span className={styles.EntityRow} key={i}>
                            <img src={iconFor(name, i)} height={'14px'} alt="" />
                            <span
                                className={styles.EntityName}
                                title={i === 0 && primaryName ? `${name}${designationSuffix || ''}` : name}
                                onMouseEnter={
                                    showOwnTooltip && i === 0
                                        ? (e) =>
                                              dispatch(
                                                  setTooltipContent({
                                                      content: `${name}${designationSuffix || ''}`,
                                                      target: e.currentTarget,
                                                      placement: 'top',
                                                  }),
                                              )
                                        : undefined
                                }
                                onMouseLeave={showOwnTooltip && i === 0 ? () => removeTooltipContent(dispatch) : undefined}
                            >
                                {name}
                            </span>
                            {isLastVisible && overflowCount > 0 && (
                                <span
                                    className={styles.MoreText}
                                    onMouseEnter={(e) =>
                                        dispatch(setTooltipContent({ content: fullListTooltip, target: e.currentTarget, placement: 'top' }))
                                    }
                                    onMouseLeave={() => removeTooltipContent(dispatch)}
                                >
                                    +{overflowCount} more
                                </span>
                            )}
                        </span>
                    );
                })}
            </div>
        );
    };

    const statusPill = (status?: string) => {
        const label = status?.trim();
        if (!label) return null;
        const tone =
            label === STATUS.DISPOSED.value
                ? styles.StatusPill_Disposed
                : label === STATUS.PENDING.value
                ? styles.StatusPill_Pending
                : styles.StatusPill_Neutral;
        return <span className={clsx(styles.StatusPill, tone)}>{label}</span>;
    };

    const getRowItemContent = (
        columnKey: string,
        legalCase,
        singleCase,
        sectionType: Direction,
        interactive: boolean,
        groupId?: GroupId,
    ) => {
        switch (columnKey) {
            case HEADERS.CASE_NUM: {
                if (isDefaulterCase(singleCase)) return <span>{DEFAULTER_EMPTY_VALUE}</span>;
                const pill = groupId === 'OTHER' ? statusPill(singleCase?.status) : null;
                const body = interactive ? (
                    <button
                        type="button"
                        className={styles.CaseNumLink}
                        onClick={(e) => {
                            e.stopPropagation();
                            openCaseDrawer(singleCase);
                        }}
                    >
                        {singleCase?.caseNum || '-'}
                    </button>
                ) : (
                    <span>{singleCase?.caseNum || '-'}</span>
                );
                return pill ? (
                    <span className={styles.CaseNumCell}>
                        {body}
                        {pill}
                    </span>
                ) : (
                    body
                );
            }
            case HEADERS.CASE_CATEGORY: {
                const category = caseCategoryOf(singleCase);
                if (isDefaulterCase(singleCase))
                    return (
                        <span className={styles.DefaulterType}>
                            <span className={clsx(styles.CaseType, CASE_TYPE_TONE_CLASS[caseCategoryTone(category)])}>
                                {singleCase.standardCaseType || DEFAULTER_EMPTY_VALUE}
                            </span>
                            <span className={styles.DefaulterAmount}>{formatDefaulterAmount(singleCase.defaulterAmount)}</span>
                        </span>
                    );
                const label = caseCategoryLabel(category);
                if (!label)
                    return <span>{convertToFirstLetterCapital(singleCase?.standardCaseType || singleCase?.caseCategory || '-')}</span>;
                return <span className={clsx(styles.CaseType, CASE_TYPE_TONE_CLASS[caseCategoryTone(category)])}>{label}</span>;
            }
            case HEADERS.COURT: {
                const locationDetails = [singleCase?.district, singleCase?.state].filter(
                    (value) => value && value.trim && value.trim() !== '',
                );
                const courtText = singleCase?.court || '-';
                return <span title={locationDetails.length ? `${courtText} (${locationDetails.join(', ')})` : courtText}>{courtText}</span>;
            }
            case HEADERS.DATE:
                return (
                    <span
                        style={{
                            padding: isPDFReport ? '10px 0' : '',
                        }}
                    >
                        {getDisplayDate(singleCase)}
                    </span>
                );
            case HEADERS.FILED_AGAINST:
            case HEADERS.FILED_BY: {
                const isActiveColumn =
                    (columnKey === HEADERS.FILED_AGAINST && sectionType === 'filed_against') ||
                    (columnKey === HEADERS.FILED_BY && sectionType === 'filed_by');
                const rawValue = singleCase?.[columnKey];
                const icon = legalCase.name === 'Company' ? companyBlack : userBlack;
                // Filed Against lists the respondents; Filed By lists the petitioners.
                const parties =
                    columnKey === HEADERS.FILED_AGAINST
                        ? singleCase?.caseDetails?.respondentAndAdvocate
                        : singleCase?.caseDetails?.petitionerAndAdvocate;

                if (!isActiveColumn) {
                    // Secondary (non-active-direction) column — no known primary entity, so we don't know
                    // whose name comes first (see renderEntityCell doc).
                    return renderEntityCell(rawValue, userBlack, undefined, undefined, parties);
                }

                const primaryName =
                    (rawValue?.includes('*') ? '*' : '') +
                    convertToFirstLetterCapital(legalCase.name === 'Company' ? companyName : legalCase.name);
                // Company rows never show a designation today, so they never show shareholding
                // either — matching Related Party/Credit Bureau, which skip it for non-person rows.
                const designationAndShareholding =
                    legalCase.name !== 'Company'
                        ? formatDesignationWithShareholding(singleCase?.groupDesignation, singleCase?.groupShareholdingPercentage, {
                              hideWhenNull: true,
                          })
                        : '';
                const designationSuffix = designationAndShareholding ? ` (${designationAndShareholding})` : '';
                return renderEntityCell(rawValue, icon, primaryName, designationSuffix, parties);
            }
            default:
                return (
                    <span
                        style={{
                            padding: isPDFReport ? '10px 0' : '',
                        }}
                        title={convertToFirstLetterCapital(decodeHtmlEntities(singleCase?.[columnKey]) || '-')}
                    >
                        {convertToFirstLetterCapital(decodeHtmlEntities(singleCase?.[columnKey]) || '-')}
                    </span>
                );
        }
    };

    const toggleGroup = (groupId: GroupId) => {
        setGroupExpanded(groupId, expandedGroup !== groupId);
    };

    const renderGroupHeaderRow = (groupId: GroupId, title: string, count: number, colSpan: number, interactive: boolean) => (
        <tr
            className={clsx(styles.GroupHeaderRow, { [styles.GroupHeaderRow_Clickable]: interactive })}
            key={`group-${groupId}`}
            ref={interactive ? sectionRef(groupId) : undefined}
            onClick={interactive ? () => toggleGroup(groupId) : undefined}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-expanded={interactive ? expandedGroup === groupId : undefined}
            onKeyDown={
                interactive
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleGroup(groupId);
                          }
                      }
                    : undefined
            }
        >
            {/* The <td> itself must stay a plain table-cell (no display override) or colSpan stops
                visually spanning the other columns in some browsers — the flex layout lives on the
                inner div instead. */}
            <td colSpan={colSpan} className={styles.GroupHeaderCell}>
                <div className={styles.GroupHeaderContent}>
                    <svg
                        className={clsx(styles.GroupChevron, { [styles.GroupChevron_Expanded]: !interactive || expandedGroup === groupId })}
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                        fill="none"
                    >
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className={styles.CaseGroupTitle}>{title}</span>
                    <span className={clsx(styles.CaseGroupBadge, count > 0 ? GROUP_BADGE_CLASS[groupId] : styles.CaseGroupBadge_Empty)}>
                        {count} Matches
                    </span>
                </div>
            </td>
        </tr>
    );

    const renderCellBody = (content: ReactNode, animated: boolean) =>
        animated ? (
            <div className={styles.CellWrap}>
                <div className={styles.CellClip}>{content}</div>
            </div>
        ) : (
            content
        );

    // ONE table, ONE header row, shared by all three sections — sections are full-width header rows
    // followed by their own data rows in the SAME tbody, so columns always line up. Collapsed sections
    // simply don't render their data rows (no CSS max-height tricks, no separate CollapsableDropdown).
    // showOtherGroup hides the "Other Probable Cases" section entirely for payloads that never send a
    // confidenceLevel signal (its bucket is guaranteed empty in that case anyway — see isHighConfidence).
    const renderTable = (direction: Direction, groups: Groups, interactive: boolean, showOtherGroup: boolean) => {
        const columns = getColumns(direction);
        return (
            <div
                ref={isPDFReport ? undefined : scrollContainerRef}
                className={clsx(styles.TableWrapper, { [styles.TableWrapper_Scrollable]: !isPDFReport })}
            >
                <table className={styles.Table}>
                    <thead className={styles.TableHead}>
                        <tr className={styles.TableHeadRow}>
                            {columns.map((column: Column) => (
                                <th
                                    key={column.key}
                                    className={clsx(styles.TableHeadRowItem, {
                                        [styles.TableHeadRowItem_CaseNum]: column.key === HEADERS.CASE_NUM,
                                        [styles.TableHeadRowItem_CaseType]: column.key === HEADERS.CASE_CATEGORY,
                                        [styles.TableHeadRowItem_Court]: column.key === HEADERS.COURT,
                                        [styles.TableHeadRowItem_FiledAgainst]: column.key === HEADERS.FILED_AGAINST,
                                        [styles.TableHeadRowItem_FiledBy]: column.key === HEADERS.FILED_BY,
                                        [styles.TableHeadRowItem_Date]: column.key === HEADERS.DATE,
                                        [styles.TableHeadRowItem_PDFReport]: isPDFReport,
                                    })}
                                >
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className={styles.TableBody} style={{ fontSize: isPDFReport ? '11px' : '' }}>
                        {visibleGroups(showOtherGroup).flatMap(({ id, title }) => {
                            const casesInGroup = groups[id];
                            const isExpanded = interactive ? expandedGroup === id : true;
                            const isCollapsing = interactive && collapsingGroupIds.includes(id);
                            const animated = interactive && groupAnimates(id);
                            const rows = [renderGroupHeaderRow(id, title, casesInGroup.length, columns.length, interactive)];

                            if (!isExpanded && !isCollapsing) return rows;

                            const rowClasses = {
                                [styles.TableBodyRow_Revealed]: animated,
                                [styles.TableBodyRow_Collapsing]: isCollapsing,
                            };

                            if (!casesInGroup.length) {
                                rows.push(
                                    <tr className={clsx(styles.TableBodyRow, rowClasses)} key={`empty-${id}`}>
                                        <td colSpan={columns.length} className={clsx(styles.TableBodyRowItem, styles.Nodata)}>
                                            {renderCellBody('No match found', animated)}
                                        </td>
                                    </tr>,
                                );
                                return rows;
                            }

                            casesInGroup.forEach((singleCase, idx) => {
                                const uniqueKey = `${direction}-${id}-${idx}`;
                                const legalCase = { name: singleCase.groupName };
                                const rowInteractive = interactive && !isDefaulterCase(singleCase);
                                rows.push(
                                    <tr
                                        className={clsx(styles.TableBodyRow, rowClasses, {
                                            [styles.TableBodyRow_Clickable]: rowInteractive,
                                            [styles.TableBodyRow_Dim]: id === 'OTHER',
                                        })}
                                        key={uniqueKey}
                                        data-lc-director={normalizeNameKey(singleCase.groupName)}
                                        onClick={rowInteractive ? () => openCaseDrawer(singleCase) : undefined}
                                    >
                                        {columns.map((column) => (
                                            <td
                                                key={column.key}
                                                className={clsx(styles.TableBodyRowItem, {
                                                    [styles.TableBodyRowItem_PDFReport]: isPDFReport,
                                                    [styles.TableBodyRowItem_Date]: column.key === HEADERS.DATE,
                                                })}
                                            >
                                                {renderCellBody(
                                                    getRowItemContent(column.key, legalCase, singleCase, direction, rowInteractive, id),
                                                    animated,
                                                )}
                                            </td>
                                        ))}
                                    </tr>,
                                );
                            });
                            return rows;
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const noteText = (
        <>
            <span className={styles.NoteLabel}>Note:</span> {LEGAL_AND_REGULATORY_NOTE_BODY}
        </>
    );

    const headerRight = (
        <div className={styles.HeaderControls}>
            <span className={styles.HeaderIndicator} data-testid={isFetchFailed ? 'fetch-failure-indicator' : undefined}>
                {isPolling && <LoadingSpinner color="#6021B3" height="1.125rem" />}
                {isPolling && <span className={styles.Message}>{LEGAL_CASES_FETCHING_TEXT}</span>}
                {isFetchFailed && (
                    <>
                        <FetchFailureIcon className={styles.FailureIcon} />
                        <span className={clsx(styles.Message, styles.Failure)}>{FETCH_FAILURE_TEXT}</span>
                    </>
                )}
            </span>
            {lastFetchedDate != null ? <div className={styles.UpdateDate}>Last Updated on {lastFetchedDate}</div> : null}
            {!isFetchFailed && (
                <CtaPill
                    tone="blue"
                    icon={changeCircleBlue}
                    label={EXCEL_ANALYSIS_LABEL}
                    loadingLabel={EXCEL_ANALYSIS_LOADING_LABEL}
                    loading={generatingExcel}
                    onClick={handleViewExcel}
                />
            )}
        </div>
    );

    const titleWithToggle = (
        <div className={styles.TitleRow}>
            <span>Legal Cases</span>
            {!isFetchFailed && (
                <CategorySelector
                    categoryOptions={directionOptions}
                    selectedCategory={activeDirection}
                    changeSelectedTab={(category) => setActiveDirection(category as Direction)}
                    categoryStyle={{ fontFamily: 'var(--text-gilroy-medium)' }}
                    equalWidth
                />
            )}
        </div>
    );

    // Failure state (delta 6) collapses the card to its header row — no bar, table, or footnote.
    const cardBodyContent = !isFetchFailed && (
        <>
            <CaseTypeBar cases={activeCaseTypeBarCases} />
            {renderTable(activeDirection, activeGroups, true, hasConfidenceData)}
            <div className={styles.Note}>{noteText}</div>
        </>
    );

    const selectedCaseDetails = useMemo(() => (caseForDetails ? mapCaseToDetails(caseForDetails) : null), [caseForDetails]);

    const render = (
        <div className={clsx(styles.LegalCases)}>
            {renderLegalCaseSummaryCards()}
            {renderRiskIndicators()}
            <div className={styles.Container}>
                <HeaderBodyBox title={titleWithToggle} headerRight={headerRight} isPDFReport={isPDFReport} hideBody={isFetchFailed}>
                    {cardBodyContent}
                </HeaderBodyBox>
            </div>
            {!!selectedCase && (
                <CaseDetailsDrawer
                    open
                    onClose={closeCaseDrawer}
                    caseDetails={selectedCaseDetails}
                    isLoadingDetails={isFetchingCaseDetails}
                    detailsFetchFailed={caseDetailsFetchFailed}
                    onDocumentClick={(docId) => viewSingleCaseDocument(docId, selectedCaseDetails?.caseDocuments ?? [])}
                    onDownloadAllClick={() =>
                        downloadCaseFunction(selectedCase.docId ?? [], selectedCase.caseNum, selectedCase.caseCategory)
                    }
                />
            )}
            {!!viewingDocId && (
                <CaseDocumentPreviewer
                    docIdToView={viewingDocId}
                    setDocIdToView={setViewingDocId}
                    label={viewingDocLabel}
                    onDownload={() => trackCaseDownload(selectedCase?.caseCategory)}
                />
            )}
            {!!viewerState && (
                <FileViewerDrawer
                    heading={`Legal Cases — ${companyName || 'Report'}`}
                    fileUrl={viewerState.url}
                    mimeType={EXT_MIME.xlsx}
                    downloading={false}
                    downloadLabel="Download"
                    onDownload={handleDownloadFromDrawer}
                    onClose={closeViewer}
                />
            )}
        </div>
    );

    if (isPDFReport)
        return (
            <>
                <A4Page sectionName={REPORT_SECTION_NAMES.LEGAL_CASES}>
                    <div className={styles.PDFTitle}>Legal Cases</div>
                    <AicaOverview module={'legalChecks'} isPDFReport style={{ marginBottom: '24px' }} />
                    <div className={clsx(styles.Container, styles.PDFReportContainer)}>
                        <HeaderBodyBox title="Legal cases filed against Company / Director" isPDFReport>
                            <CaseTypeBar cases={filedAgainstCaseTypeBarCases} />
                            {renderTable('filed_against', groupsFiledAgainst, false, hasConfidenceData)}
                            <div className={styles.Note}>{noteText}</div>
                        </HeaderBodyBox>
                    </div>
                </A4Page>
                <A4Page sectionName={REPORT_SECTION_NAMES.LEGAL_CASES}>
                    <div className={clsx(styles.Container, styles.PDFReportContainer)}>
                        <HeaderBodyBox title="Legal cases filed by Company / Director" isPDFReport>
                            <CaseTypeBar cases={filedByCaseTypeBarCases} />
                            {renderTable('filed_by', groupsFiledBy, false, hasConfidenceData)}
                            <div className={styles.Note}>{noteText}</div>
                        </HeaderBodyBox>
                    </div>
                </A4Page>
                {renderRiskIndicators()}
            </>
        );
    else return render;
}

export default LegalCases;
